/**
 * Document render modes — the type-safe bridge between document types and their
 * visual presentation layers.
 *
 * Each of the 12 registered document types (`DOC_TYPES` in `src/domain/docTypes.ts`)
 * maps to a `DocRenderMode`. The mode serves two purposes in Phase 0:
 *
 * 1. A CSS hook (`doc-mode-<mode>`) on the `.doc-page` wrapper, so future phases
 *    can add per-type styling without touching the shared renderer.
 * 2. A semantic identifier that future rendering strategies can branch on, rather
 *    than string-comparing `docType` fragments inside templates.
 *
 * The mode exists as its own module — not declared inline in `renderA4.ts` — so that
 * the unit and E2E test suites can import it without pulling in the full renderer.
 * A test that checks "CREDIT_NOTE maps to credit-note mode" should not need to
 * instantiate the entire A4 stylesheet.
 *
 * NO BUSINESS LOGIC LIVES HERE. This is a projection from a type to a presentation
 * class — it never reads from the database, never computes a figure, and never
 * decides whether a document is valid. It is a lookup table with type safety.
 */
import type { DocType } from '@domain/docTypes';

export type DocRenderMode =
  | 'sales-invoice'
  | 'quotation'
  | 'proforma'
  | 'sales-order'
  | 'purchase-order'
  | 'delivery-note'
  | 'purchase-receipt'
  | 'credit-note'
  | 'debit-note'
  | 'sales-return'
  | 'purchase-return'
  | 'purchase-invoice';

const DOC_TYPE_TO_MODE: Record<DocType, DocRenderMode> = {
  SALES_INVOICE: 'sales-invoice',
  QUOTATION: 'quotation',
  PROFORMA: 'proforma',
  SALES_ORDER: 'sales-order',
  DELIVERY_NOTE: 'delivery-note',
  CREDIT_NOTE: 'credit-note',
  SALES_RETURN: 'sales-return',
  PURCHASE_ORDER: 'purchase-order',
  PURCHASE_RECEIPT: 'purchase-receipt',
  PURCHASE_INVOICE: 'purchase-invoice',
  DEBIT_NOTE: 'debit-note',
  PURCHASE_RETURN: 'purchase-return',
};

/**
 * Resolve a document type to its render mode.
 *
 * Returns `'unknown'` for unrecognised types — the same fallback the title and
 * number-label maps already use, so a future 13th document type does not crash
 * the renderer while a developer wires it up.
 */
export function modeForDocType(docType: string): DocRenderMode | 'unknown' {
  return DOC_TYPE_TO_MODE[docType as DocType] ?? 'unknown';
}

/**
 * The CSS class added to `.doc-page` for this document type.
 *
 * Consumers in the renderer use this rather than string-concatenating the mode,
 * so a change to the class prefix lives in exactly one place.
 */
export function modeClass(docType: string): string {
  const mode = modeForDocType(docType);
  return mode === 'unknown' ? '' : `doc-mode-${mode}`;
}

/**
 * CSS accent colour per render mode.
 *
 * These are DEFINED here as data and emitted into `a4Stylesheet()`, but no CSS
 * rule in Phase 0 consumes `--doc-accent-color`. The variable exists so a future
 * phase can add `border-top: var(--doc-accent-color)` without a second lookup
 * table drifting from this one.
 *
 * Colours are chosen by document family, not by document type, so that a reader
 * learns "credit notes are red" rather than "this specific credit note is red
 * and that one is burgundy."
 */
export const MODE_ACCENT_COLOR: Record<DocRenderMode, string> = {
  'sales-invoice': '#05966a',
  'quotation': '#2563eb',
  'proforma': '#ca8a04',
  'sales-order': '#7c3aed',
  'purchase-order': '#7c3aed',
  'delivery-note': '#0ea5e0',
  'purchase-receipt': '#0ea5e0',
  'credit-note': '#dc2626',
  'debit-note': '#ea580c',
  'sales-return': '#8b5cf6',
  'purchase-return': '#8b5cf6',
  'purchase-invoice': '#05966a',
};
